import * as React from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { automationValidateQueryAtom } from '../../../_graphql/queryAutomationValidate';
import {
  $editorNodes,
  $editorEdges,
} from '../../../_state/automationEditor.state';
import {
  $validationResult,
  $hasValidationErrors,
  $issuesByNodeId,
  $errorCountByNodeId,
  $warningCountByNodeId,
} from '../../../_state/flowValidation.state';

export type UseFlowValidationOptions = {
  enabled?: boolean;
  debounceMs?: number;
};

/**
 * Hook to validate flow changes with debouncing.
 * Automatically validates when nodes or edges change.
 */
export const useFlowValidation = (options: UseFlowValidationOptions = {}) => {
  const { enabled = true, debounceMs = 300 } = options;

  const nodes = useAtomValue($editorNodes);
  const edges = useAtomValue($editorEdges);
  const setValidationResult = useSetAtom($validationResult);

  // Extract only validation-relevant data to avoid triggering on position/selection changes
  const validationHash = React.useMemo(() => {
    const nodeData = nodes.map((n) => ({
      id: n.id,
      type: n.type,
      config: n.data?.config,
    }));
    const edgeData = edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
    }));
    return JSON.stringify({ nodes: nodeData, edges: edgeData });
  }, [nodes, edges]);

  // Create query atom instance - use validationHash to avoid recreating on position/selection changes
  const queryAtom = React.useMemo(
    () =>
      automationValidateQueryAtom(() => ({
        variables: { nodes, edges },
        skip: !enabled || nodes.length === 0,
      })),

    [validationHash, enabled, nodes.length],
  );

  const queryResult = useAtomValue(queryAtom);

  // Store refetch in a ref to avoid circular dependency in useEffect
  const refetchRef = React.useRef<(() => void) | undefined>(undefined);
  refetchRef.current = () => queryResult.refetch();

  // Debounced validation trigger
  const validationTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (!enabled) {
      setValidationResult(null);
      return;
    }

    // Clear existing timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Don't validate empty flows
    if (nodes.length === 0) {
      setValidationResult(null);
      return;
    }

    // Debounce validation
    validationTimeoutRef.current = setTimeout(() => {
      // Call refetch via ref to avoid dependency on queryResult
      refetchRef.current?.();
    }, debounceMs);

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [validationHash, enabled, debounceMs, setValidationResult]);

  // Update validation result when query completes
  React.useEffect(() => {
    if (queryResult.data?.automationValidate) {
      setValidationResult({
        valid: queryResult.data.automationValidate.valid,
        issues: queryResult.data.automationValidate.issues.map((issue) => ({
          nodeId: issue.nodeId,
          field: issue.field ?? null,
          code: issue.code,
          message: issue.message,
          severity: issue.severity as 'error' | 'warning',
        })),
      });
    }
  }, [queryResult.data, setValidationResult]);

  return {
    validationResult: useAtomValue($validationResult),
    hasErrors: useAtomValue($hasValidationErrors),
    issuesByNodeId: useAtomValue($issuesByNodeId),
    errorCountByNodeId: useAtomValue($errorCountByNodeId),
    warningCountByNodeId: useAtomValue($warningCountByNodeId),
    isValidating: queryResult.isLoading,
  };
};
